import { NetworkStatus } from '@apollo/client';
import { useWeldDetailById, WeldActions, WeldDetailItem, WeldHeader } from '@bechtel/client-weld-common';
import { Box, Button, Icon, Spinner, SpinnerSize, Text, ToastAction, ToastType, useToastsDispatch } from '@bechtel/gds';
import { Weld } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { get } from 'lodash';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { WeldDetailItemContainer } from './styled';

interface RouteParams {
  id: string;
}

export const WeldDetail = () => {
  const { id } = useParams<RouteParams>();
  const weldId = id ? decodeURIComponent(id) : '';
  const activityLimit = 10;
  const dispatchToast = useToastsDispatch();

  const [getWeldDetailById, { loading, data, error, refetch, fetchMore, networkStatus }] = useWeldDetailById(true);
  useEffect(() => {
    if (!weldId) return;
    getWeldDetailById({
      variables: {
        ids: [weldId],
        paging: {
          cursor: '1',
          limit: activityLimit,
        },
      },
    });
  }, [weldId, getWeldDetailById]);

  const weldItem: Weld = get(data, 'commoditiesByIds[0].weld');

  // Show toast if we have both error and data
  useEffect(() => {
    if (error && weldItem) {
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.WARNING,
          content: t`Weld details may not have loaded completely due to a system error. The information shown may be incomplete.`,
          autoDismissTimeout: 3000,
        },
      };
      dispatchToast(toastAction);
    }
  }, [error, weldItem, dispatchToast]);

  return (
    <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
      {weldItem && (
        <>
          <WeldHeader weld={weldItem} />
          <WeldActions weld={weldItem} />
          <WeldDetailItemContainer>
            <WeldDetailItem weld={weldItem} fetchMore={fetchMore} loading={loading} />
          </WeldDetailItemContainer>
        </>
      )}
      {(networkStatus === NetworkStatus.refetch || loading) && (
        <Box
          p={{ sm: '500' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          css={'height: 100%;'}
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && error && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
        >
          <Box pb={{ sm: '500' }}>
            <Text>
              <Trans>There was a problem loading Welds Details. Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Button onClick={() => refetch()}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            <Trans>Retry</Trans>
          </Button>
        </Box>
      )}
    </Box>
  );
};
